/**
 * Master Xiangqi AI Engine v2 — Conic Chess Research Edition
 *
 * Improvements:
 *  ✅ Quiescence Search — ổn định điểm số sau nước ăn quân
 *  ✅ Repetition Detection — phát hiện và tránh lặp nước 3 lần
 *  ✅ PV Line Generation — tạo biến cờ dự kiến 4-5 nước
 *  ✅ Smart Tactical Labels — label dựa vào bản chất thực
 *  ✅ Honest Score Display — #M3 cho sát cục
 *  ✅ Adaptive Endgame Depth — quét sâu hơn khi ít quân
 *  ✅ PST (Piece Square Tables) — đánh giá vị trí chính xác hơn
 */

import {
  getLegalMoves, makeMove, isInCheck, isRed, isBlack, parseFen, findKing,
  moveToVietnameseFull, moveToVietnamese, moveToChinese, moveObjToUci
} from './XiangqiLogic.js';

const PIECE_VALS = {
  'k': 10000, 'K': 10000,
  'r': 900,   'R': 900,
  'c': 450,   'C': 450,
  'n': 400,   'N': 400,
  'b': 200,   'B': 200,
  'e': 200,   'E': 200,
  'a': 200,   'A': 200,
  'p': 150,   'P': 150
};

// PST for Knights (row 0 = black home, row 9 = red home)
const PST_N = [
  [ 0, 4, 8, 8, 4, 8, 8, 4, 0],
  [ 4, 8,16,12, 4,12,16, 8, 4],
  [ 4,12,16,20,12,20,16,12, 4],
  [ 6,12,20,24,20,24,20,12, 6],
  [ 4,16,20,24,20,24,20,16, 4],
  [ 4,12,16,20,20,20,16,12, 4],
  [ 0, 4,12,12, 8,12,12, 4, 0],
  [ 0, 4, 8, 8, 4, 8, 8, 4, 0],
  [ 0, 0, 4, 4, 0, 4, 4, 0, 0],
  [ 0, 0, 0, 4, 4, 4, 0, 0, 0]
];

// PST for Rooks
const PST_R = [
  [14,14,14,18,22,18,14,14,14],
  [14,18,16,22,26,22,16,18,14],
  [12,12,12,18,26,18,12,12,12],
  [12,14,14,18,26,18,14,14,12],
  [10,12,12,16,22,16,12,12,10],
  [ 8,10, 8,14,18,14, 8,10, 8],
  [ 6, 8, 8,12,14,12, 8, 8, 6],
  [ 6, 6, 6,10,12,10, 6, 6, 6],
  [ 6, 6, 6, 8,10, 8, 6, 6, 6],
  [ 6, 6, 6, 6, 6, 6, 6, 6, 6]
];

function getPST(piece, r, c) {
  if (piece === 'N') return PST_N[r]?.[c] || 0;
  if (piece === 'n') return PST_N[9-r]?.[8-c] || 0;
  if (piece === 'R') return PST_R[r]?.[c] || 0;
  if (piece === 'r') return PST_R[9-r]?.[8-c] || 0;
  return 0;
}

export function evaluateBoard(board) {
  let redScore = 0;
  let blackScore = 0;

  const redKing = findKing(board, 'red');
  const blackKing = findKing(board, 'black');

  if (redKing && blackKing && redKing.c === blackKing.c) {
    let piecesBetween = 0;
    for (let r = Math.min(redKing.r, blackKing.r) + 1; r < Math.max(redKing.r, blackKing.r); r++) {
      if (board[r][redKing.c]) piecesBetween++;
    }
    if (piecesBetween === 0) redScore += 400;
  }

  for (let r = 0; r < 10; r++) {
    for (let c = 0; c < 9; c++) {
      const piece = board[r][c];
      if (!piece) continue;

      let val = (PIECE_VALS[piece] || 0) + getPST(piece, r, c);

      if (piece === 'P') {
        if (r <= 4) val += 150;
        if (r <= 2) val += 110;
        if (c >= 3 && c <= 5) val += 50;
        if (blackKing && Math.abs(r - blackKing.r) + Math.abs(c - blackKing.c) <= 2) val += 170;
      } else if (piece === 'p') {
        if (r >= 5) val += 150;
        if (r >= 7) val += 110;
        if (c >= 3 && c <= 5) val += 50;
        if (redKing && Math.abs(r - redKing.r) + Math.abs(c - redKing.c) <= 2) val += 170;
      }

      if (piece === 'C') {
        if (c === 4) val += 100;
        if (r <= 4) val += 25;
      } else if (piece === 'c') {
        if (c === 4) val += 100;
        if (r >= 5) val += 25;
      }

      if ((piece === 'A' || piece === 'a') && c === 4) val += 25;
      if ((piece === 'B' || piece === 'b' || piece === 'E' || piece === 'e') && c === 4) val += 35;

      if (isRed(piece)) redScore += val;
      else blackScore += val;
    }
  }

  if (isInCheck(board, 'black')) redScore += 200;
  if (isInCheck(board, 'red')) blackScore += 200;

  return redScore - blackScore;
}

function qSearch(board, alpha, beta, isMaximizing, qDepth) {
  searchNodeCount++;
  if (searchNodeCount > 10000 || qDepth <= 0) return evaluateBoard(board);

  const standPat = evaluateBoard(board);
  if (isMaximizing) {
    if (standPat >= beta) return beta;
    alpha = Math.max(alpha, standPat);
  } else {
    if (standPat <= alpha) return alpha;
    beta = Math.min(beta, standPat);
  }

  const turn = isMaximizing ? 'red' : 'black';
  const captures = getLegalMoves(board, turn).filter(m => m.captured);
  captures.sort((a, b) => (PIECE_VALS[b.captured] || 0) - (PIECE_VALS[a.captured] || 0));

  if (isMaximizing) {
    let maxEval = standPat;
    for (const move of captures) {
      const nextBoard = makeMove(board, move);
      const e = qSearch(nextBoard, alpha, beta, false, qDepth - 1);
      maxEval = Math.max(maxEval, e);
      alpha = Math.max(alpha, e);
      if (beta <= alpha) break;
    }
    return maxEval;
  } else {
    let minEval = standPat;
    for (const move of captures) {
      const nextBoard = makeMove(board, move);
      const e = qSearch(nextBoard, alpha, beta, true, qDepth - 1);
      minEval = Math.min(minEval, e);
      beta = Math.min(beta, e);
      if (beta <= alpha) break;
    }
    return minEval;
  }
}

function boardHash(board) {
  let h = '';
  for (let r = 0; r < 10; r++)
    for (let c = 0; c < 9; c++)
      h += (board[r][c] || '.');
  return h;
}

const ttCache = new Map();
let searchNodeCount = 0;
const repTable = new Map();

function alphaBeta(board, depth, alpha, beta, isMaximizing, maxDepth) {
  searchNodeCount++;
  if (searchNodeCount > 15000) return evaluateBoard(board);

  const turn = isMaximizing ? 'red' : 'black';
  const hash = boardHash(board);
  const repCount = repTable.get(hash) || 0;
  if (repCount >= 2) return 0;

  const moves = getLegalMoves(board, turn);

  if (moves.length === 0) {
    return isMaximizing ? (-99000 + (maxDepth - depth)) : (99000 - (maxDepth - depth));
  }

  if (depth <= 0) {
    return evaluateBoard(board);
  }

  moves.sort((a, b) => {
    const capDiff = (PIECE_VALS[b.captured] || 0) - (PIECE_VALS[a.captured] || 0);
    if (capDiff !== 0) return capDiff;
    const nb = makeMove(board, b);
    const na = makeMove(board, a);
    const oppTurn = turn === 'red' ? 'black' : 'red';
    return (isInCheck(nb, oppTurn) ? 1 : 0) - (isInCheck(na, oppTurn) ? 1 : 0);
  });

  repTable.set(hash, repCount + 1);
  let result;

  if (isMaximizing) {
    let maxEval = -Infinity;
    for (const move of moves) {
      const nextBoard = makeMove(board, move);
      const e = alphaBeta(nextBoard, depth - 1, alpha, beta, false, maxDepth);
      maxEval = Math.max(maxEval, e);
      alpha = Math.max(alpha, e);
      if (beta <= alpha) break;
    }
    result = maxEval;
  } else {
    let minEval = Infinity;
    for (const move of moves) {
      const nextBoard = makeMove(board, move);
      const e = alphaBeta(nextBoard, depth - 1, alpha, beta, true, maxDepth);
      minEval = Math.min(minEval, e);
      beta = Math.min(beta, e);
      if (beta <= alpha) break;
    }
    result = minEval;
  }

  repTable.set(hash, repCount);
  return result;
}

function buildPvLine(board, turn, pvDepth) {
  const pvMoves = [];
  let currentBoard = board;
  let currentTurn = turn;

  for (let i = 0; i < pvDepth; i++) {
    const legalMoves = getLegalMoves(currentBoard, currentTurn);
    if (legalMoves.length === 0) break;

    legalMoves.sort((a, b) => (PIECE_VALS[b.captured] || 0) - (PIECE_VALS[a.captured] || 0));

    const isMax = currentTurn === 'red';
    let bestMove = legalMoves[0];
    let bestScore = isMax ? -Infinity : Infinity;

    for (const move of legalMoves.slice(0, 10)) {
      searchNodeCount++;
      if (searchNodeCount > 16000) break;
      const nextBoard = makeMove(currentBoard, move);
      const score = qSearch(nextBoard, -Infinity, Infinity, !isMax, 2);
      if ((isMax && score > bestScore) || (!isMax && score < bestScore)) {
        bestScore = score;
        bestMove = move;
      }
    }

    pvMoves.push({
      turn: currentTurn,
      move: bestMove,
      viShort: moveToVietnamese(currentBoard, bestMove, currentTurn),
      cnMove: moveToChinese(currentBoard, bestMove, currentTurn)
    });

    currentBoard = makeMove(currentBoard, bestMove);
    currentTurn = currentTurn === 'red' ? 'black' : 'red';
    if (getLegalMoves(currentBoard, currentTurn).length === 0) break;
  }

  return pvMoves;
}

function classifyTacticalStyle(cand, board, turn) {
  const { isCheckmateWin, causesCheck, isCapture, move, score } = cand;
  const isMaximizing = turn === 'red';
  const effectiveScore = isMaximizing ? score : -score;

  if (isCheckmateWin) {
    return {
      style: 'mate',
      label: '🏆 SÁT CỤC TẤT THẮNG',
      badgeColor: 'border-emerald-400 bg-gradient-to-r from-emerald-900/50 to-teal-900/50 text-emerald-200',
      description: 'Đây là nước đi duy nhất dẫn tới Chiếu Bí (Sát Cục). Đòn thắng tuyệt đối — không có đường thoát cho đối phương.',
      risk: 'Tất thắng tuyệt đối. Chỉ cần tính chính xác là kết thúc ván cờ.'
    };
  }

  if (causesCheck && isCapture) {
    return {
      style: 'check_capture',
      label: '⚡ Chiếu + Ăn Quân (Đòn Kép)',
      badgeColor: 'border-red-400 bg-gradient-to-r from-red-900/40 to-rose-900/40 text-red-200',
      description: 'Vừa chiếu Tướng vừa ăn quân trong cùng 1 nước — đây là đòn kép nguy hiểm nhất, đối phương không thể giải quyết cả hai.',
      risk: 'Áp lực tối đa. Đối phương bị ép thoát chiếu, không kịp bảo vệ quân bị ăn.'
    };
  }

  if (causesCheck) {
    return {
      style: 'check',
      label: '♟️ Chiếu Tiến Công',
      badgeColor: 'border-orange-400 bg-gradient-to-r from-orange-900/40 to-amber-900/40 text-orange-200',
      description: 'Chiếu Tướng đối phương — buộc họ mất lượt chủ động để thoát chiếu. Tạo nhịp điệu tiến công liên tục.',
      risk: 'Duy trì áp lực. Cần tính trước đòn phản chiếu hoặc phản ăn sau khi thoát chiếu.'
    };
  }

  if (isCapture) {
    const capturedVal = PIECE_VALS[move.captured] || 0;
    if (capturedVal >= 800) {
      return {
        style: 'capture_major',
        label: '⚔️ Bắt Xe (Đại Thắng)',
        badgeColor: 'border-red-400 bg-gradient-to-r from-red-950/50 to-rose-950/50 text-red-200',
        description: 'Bắt Xe đối phương — ưu thế vật chất áp đảo không thể bù đắp. Thường dẫn tới thắng ổn định trong cờ tàn.',
        risk: 'Cần đảm bảo Xe mình không bị phản bắt lại ngay.'
      };
    }
    if (capturedVal >= 400) {
      return {
        style: 'capture_medium',
        label: '⚔️ Bắt Pháo / Mã',
        badgeColor: 'border-amber-400 bg-gradient-to-r from-amber-950/50 to-yellow-950/50 text-amber-200',
        description: 'Thu được Pháo hoặc Mã đối phương — ưu thế vật chất đáng kể, lợi thế rõ ràng trong trung-tàn cuộc.',
        risk: 'Xem xét kỹ đòn đổi quân tương đương hoặc phản công của đối phương.'
      };
    }
    return {
      style: 'capture_minor',
      label: '⚔️ Giành Vật Chất',
      badgeColor: 'border-yellow-500 bg-gradient-to-r from-yellow-950/40 to-amber-950/40 text-yellow-200',
      description: 'Bắt quân nhỏ của đối phương — tích lũy ưu thế vật chất từng bước, kiên nhẫn ép tàn.',
      risk: 'Kiểm tra xem quân mình có an toàn sau nước ăn không.'
    };
  }

  if (effectiveScore >= 800) {
    return {
      style: 'dominant',
      label: '🎯 Ép Cung Áp Đảo',
      badgeColor: 'border-cyan-400 bg-gradient-to-r from-cyan-950/40 to-blue-950/40 text-cyan-200',
      description: 'Nước cờ khống chế cung Tướng đối phương — Tướng bị thu hẹp hoạt động, quân phòng thủ bị ép vào góc chết.',
      risk: 'Thế trận hoàn toàn áp đảo. Cần phối hợp thêm vài nước để tạo đòn Sát Cục.'
    };
  }

  if (effectiveScore >= 300) {
    return {
      style: 'control',
      label: '🔒 Kiểm Soát Trung Tâm',
      badgeColor: 'border-blue-400 bg-gradient-to-r from-blue-950/40 to-indigo-950/40 text-blue-200',
      description: 'Nước cờ chiếm lĩnh và kiểm soát các lộ huyết mạch — tạo ưu thế trận địa bền vững.',
      risk: 'Duy trì thế chủ động, chờ sơ hở của đối phương để tạo đột phá.'
    };
  }

  return {
    style: 'solid',
    label: '🛡️ Ổn Định Phòng Thủ',
    badgeColor: 'border-emerald-500/50 bg-emerald-950/30 text-emerald-300',
    description: 'Nước cờ củng cố trận địa — giảm thiểu rủi ro, tạo nền tảng vững chắc để phản công sau.',
    risk: 'An toàn và chắc chắn — phù hợp khi cần bảo toàn ưu thế hiện có.'
  };
}

export function isStandardOpening(board) {
  if (!board || !Array.isArray(board) || board.length !== 10) return false;
  return board[9]?.[4] === 'K' && board[0]?.[4] === 'k' &&
         board[9]?.[0] === 'R' && board[9]?.[8] === 'R' &&
         board[0]?.[0] === 'r' && board[0]?.[8] === 'r' &&
         board[9]?.[1] === 'N' && board[9]?.[7] === 'N' &&
         board[7]?.[1] === 'C' && board[7]?.[7] === 'C' &&
         board[6]?.[0] === 'P' && board[6]?.[4] === 'P';
}

export function isBlackInitialDefense(board) {
  if (!board) return false;
  return board[0]?.[4] === 'k' && board[0]?.[7] === 'n' && board[0]?.[1] === 'n' &&
         board[2]?.[1] === 'c' && board[2]?.[7] === 'c';
}

export const GRANDMASTER_OPENING_MOVES = [
  { fromR: 7, fromC: 1, toR: 7, toC: 4, name: 'Pháo 2 bình 5 (Trung Pháo)' },
  { fromR: 7, fromC: 7, toR: 7, toC: 4, name: 'Pháo 8 bình 5 (Trung Pháo)' },
  { fromR: 9, fromC: 1, toR: 7, toC: 2, name: 'Mã 2 tiến 3 (Khởi Mã)' },
  { fromR: 9, fromC: 7, toR: 7, toC: 6, name: 'Mã 8 tiến 7 (Khởi Mã)' },
  { fromR: 6, fromC: 6, toR: 5, toC: 6, name: 'Binh 7 tiến 1 (Tiên Nhân Chỉ Lộ)' },
  { fromR: 6, fromC: 2, toR: 5, toC: 2, name: 'Binh 3 tiến 1 (Tiên Nhân Chỉ Lộ)' },
  { fromR: 9, fromC: 2, toR: 7, toC: 4, name: 'Tượng 3 tiến 5 (Phi Tượng Cuộc)' },
  { fromR: 9, fromC: 6, toR: 7, toC: 4, name: 'Tượng 7 tiến 5 (Phi Tượng Cuộc)' }
];

export const OPENING_BOOK = [
  // 1. Pháo 2 bình 5 / Pháo 8 bình 5 (Trung Pháo)
  {
    condition: (b) => b[7]?.[4] === 'C' && b[0]?.[7] === 'n' && b[0]?.[1] === 'n',
    moves: [
      { fromR: 0, fromC: 7, toR: 2, toC: 6, name: 'Mã 8 tiến 7 (Bình Phong Mã)' },
      { fromR: 0, fromC: 1, toR: 2, toC: 2, name: 'Mã 2 tiến 3 (Khởi Mã)' },
      { fromR: 2, fromC: 7, toR: 2, toC: 4, name: 'Pháo 8 bình 5 (Thuận Pháo)' },
      { fromR: 2, fromC: 1, toR: 2, toC: 4, name: 'Pháo 2 bình 5 (Nghịch Pháo)' },
      { fromR: 3, fromC: 6, toR: 4, toC: 6, name: 'Tốt 7 tiến 1' }
    ]
  },
  // 2. Tiên Nhân Chỉ Lộ (Binh 7 tiến 1 / Binh 3 tiến 1)
  {
    condition: (b) => (b[5]?.[6] === 'P' || b[5]?.[2] === 'P') && b[0]?.[7] === 'n',
    moves: [
      { fromR: 3, fromC: 6, toR: 4, toC: 6, name: 'Tốt 7 tiến 1 (Đối Binh Cuộc)' },
      { fromR: 2, fromC: 1, toR: 2, toC: 2, name: 'Pháo 2 bình 3 (Tốt Để Pháo)' },
      { fromR: 2, fromC: 7, toR: 2, toC: 4, name: 'Pháo 8 bình 5 (Trung Pháo)' },
      { fromR: 0, fromC: 7, toR: 2, toC: 6, name: 'Mã 8 tiến 7' }
    ]
  },
  // 3. Khởi Mã Cuộc (Mã 2 tiến 3 / Mã 8 tiến 7)
  {
    condition: (b) => (b[7]?.[2] === 'N' || b[7]?.[6] === 'N') && b[0]?.[7] === 'n',
    moves: [
      { fromR: 3, fromC: 6, toR: 4, toC: 6, name: 'Tốt 7 tiến 1' },
      { fromR: 2, fromC: 7, toR: 2, toC: 4, name: 'Pháo 8 bình 5' },
      { fromR: 0, fromC: 7, toR: 2, toC: 6, name: 'Mã 8 tiến 7' }
    ]
  },
  // 4. Phi Tượng Cuộc (Tượng 3 tiến 5 / Tượng 7 tiến 5)
  {
    condition: (b) => b[7]?.[4] === 'E' && b[0]?.[7] === 'n',
    moves: [
      { fromR: 2, fromC: 7, toR: 2, toC: 4, name: 'Pháo 8 bình 5 (Trung Pháo phá Phi Tượng)' },
      { fromR: 3, fromC: 6, toR: 4, toC: 6, name: 'Tốt 7 tiến 1' },
      { fromR: 0, fromC: 7, toR: 2, toC: 6, name: 'Mã 8 tiến 7' }
    ]
  },
  // 5. Quá Cung Pháo (Pháo 2 bình 6 / Pháo 8 bình 4)
  {
    condition: (b) => (b[7]?.[5] === 'C' || b[7]?.[3] === 'C') && b[0]?.[7] === 'n',
    moves: [
      { fromR: 0, fromC: 7, toR: 2, toC: 6, name: 'Mã 8 tiến 7' },
      { fromR: 2, fromC: 7, toR: 2, toC: 4, name: 'Pháo 8 bình 5' }
    ]
  }
];

export function getOpeningBookMove(board, turn = 'red') {
  if (turn === 'red' && isStandardOpening(board)) {
    return GRANDMASTER_OPENING_MOVES[Math.floor(Math.random() * 4)];
  }
  if (turn === 'black') {
    for (const bookEntry of OPENING_BOOK) {
      if (bookEntry.condition(board)) {
        const legal = getLegalMoves(board, 'black');
        for (const cand of bookEntry.moves) {
          const matched = legal.find(m => m.fromR === cand.fromR && m.fromC === cand.fromC && m.toR === cand.toR && m.toC === cand.toC);
          if (matched) return matched;
        }
      }
    }
  }
  return null;
}

function countPieces(board) {
  let n = 0;
  for (let r = 0; r < 10; r++)
    for (let c = 0; c < 9; c++)
      if (board[r][c]) n++;
  return n;
}

// ══════════════════════════════════════════════════════════════════════
// WASM MATE SEARCH — Tìm sát cục cưỡng bức (forced checkmate)
// Thuật toán: Alpha-Beta chuyên biệt mate-only + iterative deepening
// ══════════════════════════════════════════════════════════════════════

const MATE_SCORE = 100000;
let mateSearchAborted = false;
let mateNodeCount = 0;
const MATE_NODE_LIMIT = 3000000;

/** Sort moves để mate search: chiêu trước, bắt quân sau, rồi mới đến thường */
function orderMovesForMate(board, moves, turn) {
  return moves.slice().sort((a, b) => {
    const oppTurn = turn === 'red' ? 'black' : 'red';
    const bAfter = makeMove(board, b);
    const aAfter = makeMove(board, a);
    const bCheck = isInCheck(bAfter, oppTurn) ? 1 : 0;
    const aCheck = isInCheck(aAfter, oppTurn) ? 1 : 0;
    if (bCheck !== aCheck) return bCheck - aCheck; // chiêu trước
    const capDiff = (PIECE_VALS[b.captured] || 0) - (PIECE_VALS[a.captured] || 0);
    return capDiff; // bắt quân to trước
  });
}

/**
 * Mate-only alpha-beta: chỉ tìm forced checkmate cho `attacker`.
 * Attacker luôn là 'red'. Defender là 'black'.
 * Returns: >= MATE_SCORE - depth nếu có mate, < 0 nếu không.
 */
function mateAlphaBeta(board, depth, alpha, beta, isAttacker, totalDepth, pvTable, pvIdx) {
  mateNodeCount++;
  if (mateSearchAborted || mateNodeCount > MATE_NODE_LIMIT) {
    mateSearchAborted = true;
    return 0;
  }

  const turn = isAttacker ? 'red' : 'black';
  const moves = getLegalMoves(board, turn);

  if (moves.length === 0) {
    if (isInCheck(board, turn)) {
      // Checkmate! Score theo độ sâu để ưu tiên mate nhanh hơn
      return isAttacker ? -(MATE_SCORE - (totalDepth - depth)) : (MATE_SCORE - (totalDepth - depth));
    }
    return 0; // Stalemate = hòa
  }

  if (depth <= 0) {
    // Tại leaf: nếu attacker đang chiếu thì cộng thêm bonus
    if (!isAttacker && isInCheck(board, 'black')) return 10;
    return 0; // Không tìm thấy mate
  }

  const ordered = orderMovesForMate(board, moves, turn);

  if (isAttacker) {
    // Red muốn maximize (tìm mate)
    let maxEval = -MATE_SCORE;
    for (const move of ordered) {
      if (mateSearchAborted) return 0;
      const nextBoard = makeMove(board, move);
      const blackMoves = getLegalMoves(nextBoard, 'black');

      if (blackMoves.length === 0 && isInCheck(nextBoard, 'black')) {
        // Direct checkmate
        const mateVal = MATE_SCORE - (totalDepth - depth);
        if (pvTable) pvTable[pvIdx] = move;
        return mateVal;
      }

      const childPv = pvTable ? [] : null;
      const e = mateAlphaBeta(nextBoard, depth - 1, alpha, beta, false, totalDepth, childPv, 0);
      if (e > maxEval) {
        maxEval = e;
        if (pvTable && e > 0) {
          pvTable[pvIdx] = move;
          if (childPv && childPv.length) pvTable.splice(pvIdx + 1, pvTable.length, ...childPv);
        }
      }
      alpha = Math.max(alpha, e);
      if (beta <= alpha) break;
    }
    return maxEval;
  } else {
    // Black muốn minimize (thoát khỏi mate)
    let minEval = MATE_SCORE;
    for (const move of ordered) {
      if (mateSearchAborted) return 0;
      const nextBoard = makeMove(board, move);
      const e = mateAlphaBeta(nextBoard, depth - 1, alpha, beta, true, totalDepth, null, 0);
      minEval = Math.min(minEval, e);
      beta = Math.min(beta, e);
      if (beta <= alpha) break;
      // Nếu black có 1 nước thoát được mate → không cần xét thêm với đoán rằng tất cả nước khác cũng thoát
      if (e <= 0) return e; // Pruning sớm: không phải forced mate
    }
    return minEval;
  }
}

/**
 * findMateWasm — Iterative deepening mate search
 * Tìm forced checkmate cho Red trong tối đa maxMoves nước.
 * Returns: { mate, mateIn, move, bestmove, moves, pv, engine } hoặc { mate: false }
 */
export function findMateWasm(board, turn = 'red', maxMoves = 10) {
  // Chỉ support tìm mate cho Red hiện tại
  if (turn !== 'red') return { mate: false };

  mateSearchAborted = false;
  mateNodeCount = 0;

  const redMoves = getLegalMoves(board, 'red');
  if (redMoves.length === 0) return { mate: false };

  // Iterative deepening: thử từ 1 đến maxMoves nước (mỗi nước Red = 2 plies)
  for (let mateIn = 1; mateIn <= maxMoves; mateIn++) {
    if (mateSearchAborted) break;
    const maxDepth = mateIn * 2 - 1; // odd: Red moves last
    const pvTable = [];

    const orderedMoves = orderMovesForMate(board, redMoves, 'red');
    let bestMove = null;
    let bestScore = -MATE_SCORE;

    for (const move of orderedMoves) {
      if (mateSearchAborted) break;
      const nextBoard = makeMove(board, move);
      const blackMoves = getLegalMoves(nextBoard, 'black');

      if (blackMoves.length === 0 && isInCheck(nextBoard, 'black')) {
        // Mate in 1!
        const uci = moveObjToUci(move);
        return {
          mate: true,
          mateIn: 1,
          move,
          bestmove: uci,
          pv: [move],
          moves: [{ move, viFull: moveToVietnameseFull(board, move, 'red'), viShort: moveToVietnamese(board, move, 'red') }],
          engine: 'WASM Mate Search',
          isNative: false
        };
      }

      const childPv = [];
      const score = mateAlphaBeta(nextBoard, maxDepth - 1, -MATE_SCORE, MATE_SCORE, false, maxDepth, childPv, 0);
      if (score > bestScore) {
        bestScore = score;
        bestMove = move;
        pvTable.length = 0;
        pvTable.push(move, ...childPv);
      }
    }

    if (bestScore >= MATE_SCORE - maxDepth - 2 && bestMove && !mateSearchAborted) {
      // Found forced mate in mateIn moves!
      const uci = moveObjToUci(bestMove);

      // Build PV move list
      const pvMoves = [];
      let b = board;
      let t = 'red';
      for (let i = 0; i < pvTable.length && pvMoves.length < mateIn * 2; i++) {
        const mv = pvTable[i];
        if (!mv) break;
        const legal = getLegalMoves(b, t);
        const found = legal.find(m => m.fromR === mv.fromR && m.fromC === mv.fromC && m.toR === mv.toR && m.toC === mv.toC);
        if (!found) break;
        pvMoves.push({
          move: found,
          viFull: moveToVietnameseFull(b, found, t),
          viShort: moveToVietnamese(b, found, t),
          turn: t
        });
        b = makeMove(b, found);
        t = t === 'red' ? 'black' : 'red';
        if (getLegalMoves(b, t).length === 0) break;
      }

      return {
        mate: true,
        mateIn,
        move: bestMove,
        bestmove: uci,
        pv: pvTable,
        moves: pvMoves,
        engine: 'WASM Mate Search',
        isNative: false
      };
    }
  }

  return { mate: false, engine: 'WASM Mate Search', isNative: false };
}

export function getBestMove(board, turn = 'red', depth = 3) {
  // 1. Check Grandmaster Opening Book first (0ms instant response)
  const bookMove = getOpeningBookMove(board, turn);
  if (bookMove) return bookMove;

  const moves = getLegalMoves(board, turn);
  if (moves.length === 0) return null;

  moves.sort((a, b) => (PIECE_VALS[b.captured] || 0) - (PIECE_VALS[a.captured] || 0));

  const pc = countPieces(board);
  const searchDepth = pc <= 6 ? 4 : 3;

  const isMaximizing = turn === 'red';
  let bestMove = moves[0];
  let bestScore = isMaximizing ? -Infinity : Infinity;

  searchNodeCount = 0;
  repTable.clear();
  let alpha = -Infinity, beta = Infinity;

  for (const move of moves) {
    const nextBoard = makeMove(board, move);
    const score = alphaBeta(nextBoard, searchDepth - 1, alpha, beta, !isMaximizing, searchDepth);
    if (isMaximizing) {
      if (score > bestScore) { bestScore = score; bestMove = move; }
      alpha = Math.max(alpha, score);
    } else {
      if (score < bestScore) { bestScore = score; bestMove = move; }
      beta = Math.min(beta, score);
    }
    if (searchNodeCount > 15000) break;
  }

  return bestMove;
}

export function analyzeStrategicOptions(board, turn = 'red', depth = 3) {
  const moves = getLegalMoves(board, turn);
  if (moves.length === 0) return [];

  const pc = countPieces(board);
  const effectiveDepth = pc <= 6 ? 7 : pc <= 10 ? 5 : pc <= 16 ? 4 : 3;
  const searchDepth = Math.max(depth || 3, effectiveDepth);

  const isMaximizing = turn === 'red';
  const scoredMoves = [];

  repTable.clear();

  for (const move of moves.slice(0, 15)) {
    searchNodeCount = 0;
    const nextBoard = makeMove(board, move);
    const score = alphaBeta(nextBoard, 2, -Infinity, Infinity, !isMaximizing, 2);
    const isCapture = !!move.captured;
    const causesCheck = isInCheck(nextBoard, turn === 'red' ? 'black' : 'red');

    const isMate = Math.abs(score) >= 90000;
    const isCheckmateWin = isMate && (isMaximizing ? score > 0 : score < 0);
    const mateMoves = isMate ? Math.max(1, Math.ceil((99000 - Math.abs(score)) / 2) + 1) : null;
    const scoreText = isCheckmateWin
      ? `#M${mateMoves}`
      : isMate
        ? `-#M${mateMoves}`
        : (score >= 0 ? `+${(score / 100).toFixed(1)}` : `${(score / 100).toFixed(1)}`);

    scoredMoves.push({
      move,
      score,
      isCapture,
      causesCheck,
      isCheckmateWin,
      isMate,
      mateMoves,
      scoreText,
      isNative: false,
      viFull: moveToVietnameseFull(board, move, turn),
      viShort: moveToVietnamese(board, move, turn),
      cnMove: moveToChinese(board, move, turn),
      uci: moveObjToUci(move)
    });
  }

  scoredMoves.sort((a, b) => isMaximizing ? (b.score - a.score) : (a.score - b.score));
  const topCandidates = scoredMoves.slice(0, 5);

  return topCandidates.map((cand, idx) => {
    const pvDepth = idx === 0 ? 4 : idx <= 1 ? 3 : 2;
    const nextBoard = makeMove(board, cand.move);
    searchNodeCount = 0;
    const pvLine = buildPvLine(nextBoard, turn === 'red' ? 'black' : 'red', pvDepth);
    const tacticalStyle = classifyTacticalStyle(cand, board, turn);

    return {
      ...cand,
      ...tacticalStyle,
      pv: pvLine,
      evalText: cand.scoreText,
      evalDepth: 2
    };
  });
}

export function solvePuzzleSequence(initialFen, maxMoves = 4, searchDepth = 3) {
  let startBoard;
  try {
    const parsed = parseFen(initialFen);
    startBoard = parsed.board;
  } catch {
    return null;
  }

  let currentBoard = startBoard;
  let currentTurn = 'red';
  const solutionMoves = [];

  for (let i = 0; i < maxMoves * 2; i++) {
    const legalMoves = getLegalMoves(currentBoard, currentTurn);
    if (legalMoves.length === 0) break;

    const bestMove = getBestMove(currentBoard, currentTurn, searchDepth);
    if (!bestMove) break;

    solutionMoves.push({
      turn: currentTurn,
      move: bestMove,
      viFull: moveToVietnameseFull(currentBoard, bestMove, currentTurn),
      viShort: moveToVietnamese(currentBoard, bestMove, currentTurn),
      cnMove: moveToChinese(currentBoard, bestMove, currentTurn),
      bBefore: currentBoard,
      bAfter: makeMove(currentBoard, bestMove)
    });

    currentBoard = makeMove(currentBoard, bestMove);
    currentTurn = currentTurn === 'red' ? 'black' : 'red';
    if (getLegalMoves(currentBoard, currentTurn).length === 0) break;
  }

  const formattedTableMoves = [];
  for (let i = 0; i < solutionMoves.length; i += 2) {
    const redPly = solutionMoves[i];
    const blkPly = solutionMoves[i + 1];
    formattedTableMoves.push({
      num: Math.floor(i / 2) + 1,
      red: redPly ? redPly.cnMove : '',
      red_vi: redPly ? redPly.viFull : '',
      red_short: redPly ? redPly.viShort : '',
      black: blkPly ? blkPly.cnMove : '',
      black_vi: blkPly ? blkPly.viFull : '',
      black_short: blkPly ? blkPly.viShort : '',
      customMoveRed: redPly?.move || null,
      customMoveBlack: blkPly?.move || null
    });
  }

  const redCount = Math.ceil(solutionMoves.length / 2);
  return {
    rawPlies: solutionMoves,
    formattedMoves: formattedTableMoves,
    redMoveCount: redCount,
    isCheckmateWin: false,
    targetGoal: `🎯 Đỏ đi trước • ${redCount} Nước Sát Cục`,
    firstMoveHint: formattedTableMoves[0]
      ? `💡 Gợi ý: Đi nước ${formattedTableMoves[0].red_vi} [${formattedTableMoves[0].red_short}]`
      : ''
  };
}
